import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, Upload, Link2, Loader2, Pencil } from "lucide-react";
import { api, errMsg, inr } from "@/lib/api";

const RESULT_STYLE = { draft: "bg-emerald-50 text-emerald-700", duplicate: "bg-amber-50 text-amber-700", error: "bg-red-50 text-red-700" };
const RESULT_LABEL = { draft: "Draft created", duplicate: "Already in Deals", error: "Not added" };

const UrlRows = ({ urls, setUrls }) => {
  const refs = useRef([]);
  const [focus, setFocus] = useState(null);
  useEffect(() => { if (focus !== null) refs.current[focus]?.focus(); }, [focus, urls.length]);
  const update = (i, v) => setUrls(urls.map((u, j) => (j === i ? v : u)));
  const add = (i) => { const next = [...urls]; next.splice(i + 1, 0, ""); setUrls(next); setFocus(i + 1); };
  const remove = (i) => setUrls(urls.length > 1 ? urls.filter((_, j) => j !== i) : [""]);
  return (
    <div className="space-y-2" data-testid="link-import-rows">
      {urls.map((u, i) => (
        <div key={i} className="flex items-center gap-2">
          <input ref={(el) => (refs.current[i] = el)} value={u} onChange={(e) => update(i, e.target.value)} placeholder="https://store.com/product-page"
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add(i))}
            className="flex-1 h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid={`link-import-url-${i}`} />
          {urls.length > 1 && <button type="button" title="Remove" onClick={() => remove(i)} className="p-2 rounded hover:bg-slate-100 text-slate-500" data-testid={`link-import-remove-${i}`}><X size={16} /></button>}
          <button type="button" title="Add another link" onClick={() => add(i)} className="h-10 w-10 shrink-0 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center" data-testid={`link-import-add-${i}`}><Plus size={18} /></button>
        </div>
      ))}
    </div>
  );
};

const Results = ({ job, onOpenDeal }) => (
  <div className="space-y-2" data-testid="link-import-results">
    <p className="text-xs font-semibold text-slate-600" data-testid="link-import-progress">{job.status === "finished" ? "Finished" : "Reading links…"} {job.done} / {job.total}</p>
    {(job.results || []).map((r, i) => r && (
      <div key={i} className="flex items-start gap-3 rounded-lg border border-slate-200 p-2" data-testid={`link-import-result-${i}`}>
        {r.image_url ? <img src={r.image_url} alt="" className="w-12 h-12 object-contain rounded bg-slate-50" /> : <div className="w-12 h-12 rounded bg-slate-100" />}
        <div className="flex-1 min-w-0 text-xs">
          <p className="font-semibold text-sm line-clamp-1">{r.title || r.url}</p>
          <p className="text-slate-500">{[r.merchant, r.price ? inr(r.price) : "", r.mrp ? `MRP ${inr(r.mrp)}` : "", r.network ? `link: ${r.network}` : ""].filter(Boolean).join(" · ")}</p>
          {r.note && <p className="text-slate-600 mt-0.5">{r.note}</p>}
        </div>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${RESULT_STYLE[r.result]}`}>{RESULT_LABEL[r.result]}</span>
        {r.deal_id && <button type="button" onClick={() => onOpenDeal(r.deal_id)} className="p-1.5 rounded border hover:bg-slate-50" title="Edit & approve" data-testid={`link-import-edit-${i}`}><Pencil size={14} /></button>}
      </div>
    ))}
  </div>
);

export const LinkImportPanel = ({ onOpenDeal }) => {
  const qc = useQueryClient();
  const [urls, setUrls] = useState([""]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!job?.id || job.status === "finished") return undefined;
    const t = setTimeout(async () => {
      const r = await api.get(`/admin/deals/from-links/${job.id}`).catch(() => null);
      if (r) { setJob(r.data); if (r.data.status === "finished") { qc.invalidateQueries({ queryKey: ["a-deals"] }); toast.success("Drafts are ready in Deals → pending review"); } }
    }, 1500);
    return () => clearTimeout(t);
  }, [job, qc]);
  const start = async (req) => {
    setBusy(true);
    try { const r = await req(); setJob({ id: r.data.job_id, total: r.data.total, done: 0, status: "running", results: [] }); setUrls([""]); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const submit = () => start(() => api.post("/admin/deals/from-links", { urls: urls.filter((u) => u.trim()) }));
  const upload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    start(() => api.post("/admin/deals/from-links/csv", fd));
    e.target.value = "";
  };
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3" data-testid="link-import-panel">
      <div>
        <p className="font-bold text-sm flex items-center gap-2"><Link2 size={16} /> Add from product links</p>
        <p className="text-xs text-slate-600">Paste product page links (press + for another). Name, image, price and MRP are filled from the store's public preview data where allowed, and your affiliate link is made automatically. Each link becomes a draft for you to edit and approve - nothing is published.</p>
      </div>
      <UrlRows urls={urls} setUrls={setUrls} />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={busy || !urls.some((u) => u.trim())} onClick={submit} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold disabled:opacity-40 flex items-center gap-2" data-testid="link-import-submit">
          {busy && <Loader2 size={14} className="animate-spin" />} Create drafts ({urls.filter((u) => u.trim()).length})
        </button>
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold flex items-center gap-2" data-testid="link-import-csv-button"><Upload size={14} /> Upload CSV of links</button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={upload} className="hidden" data-testid="link-import-csv-input" />
        <span className="text-[11px] text-slate-500">CSV: one product link per row in the first column (up to 200).</span>
      </div>
      {job && <Results job={job} onOpenDeal={onOpenDeal} />}
    </div>
  );
};
